import * as t from 'io-ts';
import _ from 'lodash';

import {parseAnswer, RAnswer} from './answerModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {parseChannel, RChannel} from './channelModel';
import {parseDelegation, RDelegation} from './delegationModel';
import {parseInbox, RInbox} from './inboxModel';
import {parsePlugin, RPlugin} from './pluginModel';
import {parseRole, RRole} from './roleModel';
import {RRuleBundleType} from './ruleBundleModel';
import {parseRule, parseRules, RRule} from './ruleModel';
import {parseSignature, RSignature} from './signatureModel';
import {parseTag, RTag} from './tagModel';
import {parseTeammate, RTeammate} from './teammateModel';
import {parseTeam, RTeam} from './teamModel';
import {parseVacationResponder, RVacationResponder} from './vacationResponderModel';

/*
 * Runtime type.
 */

export enum SettingsSearchItemTypesEnum {
  COMPANY_PREFERENCE = 'companyPreference',
  COMPANY_PREFERENCE_ROUTE = 'companyPreferenceRoute',
  COMPANY_SETTINGS_ROUTE = 'companySettingsRoute',
  CUSTOM_VIEW = 'customView',
  IMPORT_ROUTE = 'importRoute',
  INBOX = 'inbox',
  INTEGRATION = 'integration',
  PERSONAL_PREFERENCE = 'personalPreference',
  ROLE = 'role',
  RULE = 'rule',
  SETTINGS_ROUTE = 'settingsRoute',
  SIGNATURE = 'signature',
  TAG = 'tag',
  TEAM = 'team',
  TEAMMATE = 'teammate',
  TEAMMATE_ROUTE = 'teammateRoute',
  PLUGIN = 'customPlugin',
  VACATION_RESPONDER = 'vacationResponder',
  CHANNEL = 'channel',
  DELEGATION = 'delegation',
  ANSWER = 'answer',
}

const RSettingsSearchSeeAllItem = t.type({
  key: makeEnumRuntimeType(SettingsSearchItemTypesEnum),
  title: t.string,
});

const RSettingsSearchBase = t.intersection([
  t.type({
    resourceType: makeEnumRuntimeType(SettingsSearchItemTypesEnum),
  }),
  t.partial({
    seeAllItem: RSettingsSearchSeeAllItem,
  }),
]);

const RSettingsSearchAnswer = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.ANSWER),
    items: t.readonlyArray(RAnswer),
  }),
]);

const RSettingsSearchChannel = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.CHANNEL),
    items: t.readonlyArray(RChannel),
  }),
]);

const RSettingsSearchPreferenceItem = t.type({
  title: t.string,
  key: t.string,
  description: t.string,
});

const RSettingsSearchRouteItem = t.type({
  title: t.string,
  key: t.string,
  description: t.string,
});

const RSettingsSearchCompanyPreference = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.COMPANY_PREFERENCE),
    items: t.readonlyArray(RSettingsSearchPreferenceItem),
  }),
]);

const RSettingsSearchCompanyPreferenceRoute = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE),
    items: t.readonlyArray(RSettingsSearchRouteItem),
  }),
]);

const RSettingsSearchCompanySettingRoute = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE),
    items: t.readonlyArray(RSettingsSearchRouteItem),
  }),
]);

const RSettingsSearchCustomView = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.CUSTOM_VIEW),
    items: t.readonlyArray(
      t.type({
        name: t.string,
        namespace: t.string,
        id: t.Integer,
        description: t.string,
        rules: t.readonlyArray(RRule),
      }),
    ),
  }),
]);

const RSettingsSearchDelegation = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.DELEGATION),
    items: t.readonlyArray(
      t.type({
        name: t.string,
        description: t.string,
        delegation: RDelegation,
      }),
    ),
  }),
]);

const RSettingsSearchImportRoute = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.IMPORT_ROUTE),
    items: t.readonlyArray(RSettingsSearchRouteItem),
  }),
]);

const RSettingsSearchInbox = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.INBOX),
    items: t.readonlyArray(
      t.type({
        inbox: RInbox,
        channelNames: t.readonlyArray(t.string),
        ruleCount: t.Integer,
      }),
    ),
  }),
]);

const RSettingsSearchIntegration = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.INTEGRATION),
    items: t.readonlyArray(
      t.type({
        type: t.string,
        name: t.string,
        description: t.string,
      }),
    ),
  }),
]);

const RSettingsSearchPersonalPreference = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE),
    items: t.readonlyArray(RSettingsSearchPreferenceItem),
  }),
]);

const RSettingsSearchPlugin = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.PLUGIN),
    items: t.readonlyArray(RPlugin),
  }),
]);

const RSettingsSearchVacationResponder = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.VACATION_RESPONDER),
    items: t.readonlyArray(
      t.type({
        vacationResponder: RVacationResponder,
        channels: t.readonlyArray(RChannel),
      }),
    ),
  }),
]);

const RSettingsSearchRole = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.ROLE),
    items: t.readonlyArray(
      t.type({
        role: RRole,
        teammateCount: t.Integer,
      }),
    ),
  }),
]);

const RSettingsSearchRule = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.RULE),
    items: t.readonlyArray(
      t.intersection([
        t.type({
          rule: RRule,
          ruleBundleType: t.union([RRuleBundleType, t.undefined]),
          ruleBundleId: t.Integer,
          ruleBundleName: t.string,
          description: t.string,
        }),
        t.partial({
          ruleBundleRules: t.readonlyArray(RRule),
        }),
      ]),
    ),
  }),
]);

const RSettingsSearchSettingRoute = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.SETTINGS_ROUTE),
    items: t.readonlyArray(RSettingsSearchRouteItem),
  }),
]);

const RSettingsSearchSignature = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.SIGNATURE),
    items: t.readonlyArray(RSignature),
  }),
]);

const RSettingsSearchTag = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.TAG),
    items: t.readonlyArray(
      t.type({
        tag: RTag,
        description: t.string,
      }),
    ),
  }),
]);

const RSettingsSearchTeam = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.TEAM),
    items: t.readonlyArray(
      t.type({
        team: RTeam,
        inboxes: t.readonlyArray(RInbox),
      }),
    ),
  }),
]);

const RSettingsSearchTeammate = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.TEAMMATE),
    items: t.readonlyArray(RTeammate),
  }),
]);

const RSettingsSearchTeammateRoute = t.intersection([
  RSettingsSearchBase,
  t.type({
    resourceType: t.literal(SettingsSearchItemTypesEnum.TEAMMATE_ROUTE),
    items: t.readonlyArray(RSettingsSearchRouteItem),
  }),
]);

const RSettingsSearchItem = t.taggedUnion('resourceType', [
  RSettingsSearchAnswer,
  RSettingsSearchChannel,
  RSettingsSearchCompanyPreference,
  RSettingsSearchCompanyPreferenceRoute,
  RSettingsSearchCompanySettingRoute,
  RSettingsSearchCustomView,
  RSettingsSearchDelegation,
  RSettingsSearchImportRoute,
  RSettingsSearchInbox,
  RSettingsSearchIntegration,
  RSettingsSearchPersonalPreference,
  RSettingsSearchPlugin,
  RSettingsSearchRole,
  RSettingsSearchRule,
  RSettingsSearchSettingRoute,
  RSettingsSearchSignature,
  RSettingsSearchVacationResponder,
  RSettingsSearchTag,
  RSettingsSearchTeam,
  RSettingsSearchTeammate,
  RSettingsSearchTeammateRoute,
]);

const RSettingsSearchPayloadDirty = t.readonlyArray(RSettingsSearchItem);

/*
 * Type.
 */

export interface SettingsSearchPreferenceItemModel extends t.TypeOf<typeof RSettingsSearchPreferenceItem> {}
export interface SettingsSearchRouteItemModel extends t.TypeOf<typeof RSettingsSearchRouteItem> {}

export interface SettingsSearchAnswerModel extends t.TypeOf<typeof RSettingsSearchAnswer> {}
export interface SettingsSearchChannelModel extends t.TypeOf<typeof RSettingsSearchChannel> {}
export interface SettingsSearchCompanyPreferenceModel
  extends t.TypeOf<typeof RSettingsSearchCompanyPreference> {}
export interface SettingsSearchCompanyPreferenceRouteModel
  extends t.TypeOf<typeof RSettingsSearchCompanyPreferenceRoute> {}
export interface SettingsSearchCompanySettingRouteModel
  extends t.TypeOf<typeof RSettingsSearchCompanySettingRoute> {}
export interface SettingsSearchCustomViewModel extends t.TypeOf<typeof RSettingsSearchCustomView> {}
export interface SettingsSearchDelegationModel extends t.TypeOf<typeof RSettingsSearchDelegation> {}
export interface SettingsSearchImportRouteModel extends t.TypeOf<typeof RSettingsSearchImportRoute> {}
export interface SettingsSearchInboxModel extends t.TypeOf<typeof RSettingsSearchInbox> {}
export interface SettingsSearchIntegrationModel extends t.TypeOf<typeof RSettingsSearchIntegration> {}
export interface SettingsSearchPersonalPreferenceModel
  extends t.TypeOf<typeof RSettingsSearchPersonalPreference> {}
export interface SettingsSearchPluginModel extends t.TypeOf<typeof RSettingsSearchPlugin> {}
export interface SettingsSearchRoleModel extends t.TypeOf<typeof RSettingsSearchRole> {}
export interface SettingsSearchRuleModel extends t.TypeOf<typeof RSettingsSearchRule> {}
export interface SettingsSearchSeeAllItemModel extends t.TypeOf<typeof RSettingsSearchSeeAllItem> {}
export interface SettingsSearchSettingRouteModel extends t.TypeOf<typeof RSettingsSearchSettingRoute> {}
export interface SettingsSearchSignatureModel extends t.TypeOf<typeof RSettingsSearchSignature> {}
export interface SettingsSearchTagModel extends t.TypeOf<typeof RSettingsSearchTag> {}
export interface SettingsSearchTeammateModel extends t.TypeOf<typeof RSettingsSearchTeammate> {}
export interface SettingsSearchTeammateRouteModel extends t.TypeOf<typeof RSettingsSearchTeammateRoute> {}
export interface SettingsSearchTeamModel extends t.TypeOf<typeof RSettingsSearchTeam> {}
export interface SettingsSearchVacationResponderModel
  extends t.TypeOf<typeof RSettingsSearchVacationResponder> {}

export interface SettingsSearchPayloadModel extends t.TypeOf<typeof RSettingsSearchPayloadDirty> {}

export type SettingsSearchItemModel =
  | SettingsSearchAnswerModel
  | SettingsSearchChannelModel
  | SettingsSearchCompanyPreferenceModel
  | SettingsSearchCompanyPreferenceRouteModel
  | SettingsSearchCompanySettingRouteModel
  | SettingsSearchCustomViewModel
  | SettingsSearchDelegationModel
  | SettingsSearchImportRouteModel
  | SettingsSearchInboxModel
  | SettingsSearchIntegrationModel
  | SettingsSearchPersonalPreferenceModel
  | SettingsSearchPluginModel
  | SettingsSearchRoleModel
  | SettingsSearchRuleModel
  | SettingsSearchSettingRouteModel
  | SettingsSearchSignatureModel
  | SettingsSearchTagModel
  | SettingsSearchTeammateModel
  | SettingsSearchTeammateRouteModel
  | SettingsSearchTeamModel
  | SettingsSearchVacationResponderModel;

export const RSettingsSearchPayload = t.clean<SettingsSearchPayloadModel>(RSettingsSearchPayloadDirty as any);

/*
 * IO methods.
 */

type WithoutSeeAll<T extends SettingsSearchItemModel> = Omit<T, 'seeAllItem'>;

export function parseSettingsSearchPayload(src: ReadonlyArray<any>): SettingsSearchPayloadModel {
  return _(src)
    .map((arrayItem) => {
      const resourceType = parseResourceType(arrayItem.resourceType);
      const seeAllItem = parseSeeAllItem(arrayItem.seeAllItem);

      const parseSettingsSearchResource: {
        [K in SettingsSearchItemTypesEnum]: (
          src: any,
        ) => WithoutSeeAll<Extract<SettingsSearchItemModel, {resourceType: K}>>;
      } = {
        [SettingsSearchItemTypesEnum.ANSWER]: parseAnswerItem,
        [SettingsSearchItemTypesEnum.CHANNEL]: parseChannelItem,
        [SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE]: parseCompanyPreferenceRouteItem,
        [SettingsSearchItemTypesEnum.COMPANY_PREFERENCE]: parseCompanyPreferenceItem,
        [SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE]: parseCompanySettingsRoute,
        [SettingsSearchItemTypesEnum.CUSTOM_VIEW]: parseCustomViewItem,
        [SettingsSearchItemTypesEnum.DELEGATION]: parseDelegationItem,
        [SettingsSearchItemTypesEnum.IMPORT_ROUTE]: parseImportRouteItem,
        [SettingsSearchItemTypesEnum.INBOX]: parseInboxItem,
        [SettingsSearchItemTypesEnum.INTEGRATION]: parseIntegrationItem,
        [SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE]: parsePersonalPreferenceItem,
        [SettingsSearchItemTypesEnum.PLUGIN]: parsePluginItem,
        [SettingsSearchItemTypesEnum.ROLE]: parseRoleItem,
        [SettingsSearchItemTypesEnum.RULE]: parseRuleItem,
        [SettingsSearchItemTypesEnum.SETTINGS_ROUTE]: parseSettingRouteItem,
        [SettingsSearchItemTypesEnum.SIGNATURE]: parseSignatureItem,
        [SettingsSearchItemTypesEnum.TAG]: parseTagItem,
        [SettingsSearchItemTypesEnum.TEAM]: parseTeamItem,
        [SettingsSearchItemTypesEnum.TEAMMATE_ROUTE]: parseTeammateRouteItem,
        [SettingsSearchItemTypesEnum.TEAMMATE]: parseTeammateItem,
        [SettingsSearchItemTypesEnum.VACATION_RESPONDER]: parseVacationResponderItem,
      };

      const parsed = parseSettingsSearchResource[resourceType](arrayItem);

      return {
        ...parsed,
        seeAllItem,
      };
    })
    .compact()
    .value();
}

function parseAnswerItem(src: any): WithoutSeeAll<SettingsSearchAnswerModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.ANSWER,
    items: src.items.map((i: any) => parseAnswer(i)),
  };
}

function parseChannelItem(src: any): WithoutSeeAll<SettingsSearchChannelModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.CHANNEL,
    items: src.items.map((i: any) => parseChannel(i)),
  };
}

function parseCompanyPreferenceItem(src: any): WithoutSeeAll<SettingsSearchCompanyPreferenceModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.COMPANY_PREFERENCE,
    items: src.items.map((i: any) => parseSearchPreferenceItem(i)),
  };
}

function parseCompanyPreferenceRouteItem(src: any): WithoutSeeAll<SettingsSearchCompanyPreferenceRouteModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE,
    items: src.items.map((i: any) => parseSearchRouteItem(i)),
  };
}

function parseCustomViewItem(src: any): WithoutSeeAll<SettingsSearchCustomViewModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.CUSTOM_VIEW,
    items: src.items.map((i: any) => ({
      description: i.description,
      namespace: i.namespace,
      name: i.name,
      id: i.id,
      rules: parseRules(i),
    })),
  };
}

function parseDelegationItem(src: any): WithoutSeeAll<SettingsSearchDelegationModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.DELEGATION,
    items: src.items.map((i: any) => ({
      description: i.description,
      name: i.name,
      delegation: parseDelegation(i.delegation),
    })),
  };
}

function parseImportRouteItem(src: any): WithoutSeeAll<SettingsSearchImportRouteModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.IMPORT_ROUTE,
    items: src.items.map((i: any) => parseSearchRouteItem(i)),
  };
}

function parseInboxItem(src: any): WithoutSeeAll<SettingsSearchInboxModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.INBOX,
    items: src.items.map((i: any) => ({
      inbox: parseInbox(i.inbox),
      channelNames: i.channelNames,
      ruleCount: i.ruleCount,
    })),
  };
}

function parseIntegrationItem(src: any): WithoutSeeAll<SettingsSearchIntegrationModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.INTEGRATION,
    items: src.items.map((i: any) => ({
      type: i.type,
      name: i.name,
      description: i.description,
    })),
  };
}

function parsePersonalPreferenceItem(src: any): WithoutSeeAll<SettingsSearchPersonalPreferenceModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE,
    items: src.items.map((i: any) => parseSearchPreferenceItem(i)),
  };
}

function parsePluginItem(src: any): WithoutSeeAll<SettingsSearchPluginModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.PLUGIN,
    items: src.items.map((i: any) => parsePlugin(i)),
  };
}

function parseVacationResponderItem(src: any): WithoutSeeAll<SettingsSearchVacationResponderModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.VACATION_RESPONDER,
    items: src.items.map((i: any) => ({
      vacationResponder: parseVacationResponder(i.vacationResponder),
      channels: i.channels.map((c: any) => parseChannel(c)),
    })),
  };
}

function parseRoleItem(src: any): WithoutSeeAll<SettingsSearchRoleModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.ROLE,
    items: src.items.map((i: any) => ({
      role: parseRole(i.role),
      teammateCount: i.teammateCount || 0,
    })),
  };
}

function parseRuleItem(src: any): WithoutSeeAll<SettingsSearchRuleModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.RULE,
    items: src.items.map((i: any) => ({
      rule: parseRule(i.rule),
      ruleBundleType: RRuleBundleType.is(i.ruleBundleType) ? i.ruleBundleType : undefined,
      ruleBundleId: i.ruleBundleId,
      ruleBundleName: i.ruleBundleName,
      description: i.description,
      ruleBundleRules: (i.ruleBundleRules || []).map((r: any) => parseRule(r)),
    })),
  };
}

function parseSettingRouteItem(src: any): WithoutSeeAll<SettingsSearchSettingRouteModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.SETTINGS_ROUTE,
    items: src.items.map((i: any) => parseSearchRouteItem(i)),
  };
}

function parseSignatureItem(src: any): WithoutSeeAll<SettingsSearchSignatureModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.SIGNATURE,
    items: src.items.map((i: any) => parseSignature(i)),
  };
}

function parseTagItem(src: any): WithoutSeeAll<SettingsSearchTagModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.TAG,
    items: src.items.map((i: any) => ({
      tag: parseTag(i.tag),
      description: i.description,
    })),
  };
}

function parseTeamItem(src: any): WithoutSeeAll<SettingsSearchTeamModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.TEAM,
    items: src.items.map((i: any) => ({
      team: parseTeam(i.team),
      inboxes: i.inboxes.length > 0 ? i.inboxes.map((inbox: any) => parseInbox(inbox)) : [],
    })),
  };
}

function parseTeammateItem(src: any): WithoutSeeAll<SettingsSearchTeammateModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.TEAMMATE,
    items: src.items.map((i: any) => parseTeammate(i)),
  };
}

function parseTeammateRouteItem(src: any): WithoutSeeAll<SettingsSearchTeammateRouteModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.TEAMMATE_ROUTE,
    items: src.items.map((i: any) => parseSearchRouteItem(i)),
  };
}

function parseCompanySettingsRoute(src: any): WithoutSeeAll<SettingsSearchCompanySettingRouteModel> {
  return {
    resourceType: SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE,
    items: src.items.map((i: any) => parseSearchRouteItem(i)),
  };
}

/*
 * Helpers.
 */

function parseSearchRouteItem(src: any): SettingsSearchRouteItemModel {
  return {
    title: src.title,
    key: src.key,
    description: src.description,
  };
}

function parseSearchPreferenceItem(src: any): SettingsSearchPreferenceItemModel {
  return {
    title: src.title,
    key: src.key,
    description: src.description,
  };
}

function parseSeeAllItem(src: any): SettingsSearchSeeAllItemModel | undefined {
  if (!src) {
    return undefined;
  }

  return {
    key: parseResourceType(src.key),
    title: src.title,
  };
}

function parseResourceType(src: any): SettingsSearchItemTypesEnum {
  return src;
}

/*
 * API type.
 */

export const ApiSearchSettingsPayload = makeApiType(
  'searchSettingsPayload',
  RSettingsSearchPayload,
  parseSettingsSearchPayload,
);
